class VideoPlayer extends HTMLElement {
	_deferred = null;
	_posterBtn = null;
	_template = null;
	_video = null;
	_loaded = false;
	_onPosterClick = null;

	// Controls-related fields
	_controls = null;
	_playBtn = null;
	_volumeBtn = null;
	_playIcon = null;
	_pauseIcon = null;
	_volOnIcon = null;
	_volOffIcon = null;

	_onPlayClick = null;
	_onVolumeClick = null;

	_onVideoPlayEv = null;
	_onVideoPauseEv = null;
	_onVolumeChangeEv = null;

	// Meta / time-left
	_meta = null;
	_timeLeftSpan = null;

	_onTimeUpdateEv = null;
	_onLoadedMetadataEv = null;
	_onDurationChangeEv = null;
	_onEmptiedEv = null;

	_onTapToToggle = null;

	_bindPosterButton() {
		if (this._posterBtn && !this._onPosterClick) {
			this._onPosterClick = () => this.loadAndPlay();
			this._posterBtn.addEventListener("click", this._onPosterClick);
		}
	}

	_unbindPosterButton() {
		if (this._posterBtn && this._onPosterClick) {
			this._posterBtn.removeEventListener("click", this._onPosterClick);
			this._onPosterClick = null;
		}
	}

	_initControls() {
		this._controls = this.querySelector(".video-controls");
		if (!this._controls) return;

		this._playBtn = this._controls.querySelector(".play-button");
		this._volumeBtn = this._controls.querySelector(".volume-button");

		this._playIcon = this._playBtn?.querySelector(".play-icon") || null;
		this._pauseIcon = this._playBtn?.querySelector(".pause-icon") || null;

		this._volOnIcon = this._volumeBtn?.querySelector(".volume-on") || null;
		this._volOffIcon = this._volumeBtn?.querySelector(".volume-off") || null;

		this._bindControls();

		this._updatePlayUI();
		this._updateVolumeUI();
	}
	// this is only running if the controls div is inside the video-player, but the meta infos needs to be independent of that
	_bindMeta() {
		// Require both video and meta container to wire listeners
		if (!this._video || !this._meta) return;
		if (!this._onTimeUpdateEv) {
			this._onTimeUpdateEv = () => this._updateTimeLeft();
			this._video.addEventListener("timeupdate", this._onTimeUpdateEv);
		}
		if (!this._onLoadedMetadataEv) {
			this._onLoadedMetadataEv = () => this._updateTimeLeft();
			this._video.addEventListener("loadedmetadata", this._onLoadedMetadataEv);
		}
		if (!this._onDurationChangeEv) {
			this._onDurationChangeEv = () => this._updateTimeLeft();
			this._video.addEventListener("durationchange", this._onDurationChangeEv);
		}
		if (!this._onEmptiedEv) {
			this._onEmptiedEv = () => this._updateTimeLeft();
			this._video.addEventListener("emptied", this._onEmptiedEv);
		}
	}

	_bindControls() {
		if (!this._controls) return;
		if (this._playBtn && !this._onPlayClick) {
			this._onPlayClick = () => this.toggle();
			this._playBtn.addEventListener("click", this._onPlayClick);
		}
		if (this._volumeBtn && !this._onVolumeClick) {
			this._onVolumeClick = () => {
				if (!this._loaded) this.load();
				if (this._video) {
					const willMute = !this._video.muted;
					this._video.muted = willMute;

					if (willMute) this.setAttribute("muted", "");
					else this.removeAttribute("muted");
					this._updateVolumeUI();
				}
			};
			this._volumeBtn.addEventListener("click", this._onVolumeClick);
		}
		if (this._video) {
			if (!this._onVideoPlayEv) {
				this._onVideoPlayEv = () => this._updatePlayUI();
				this._video.addEventListener("play", this._onVideoPlayEv);
			}
			if (!this._onVideoPauseEv) {
				this._onVideoPauseEv = () => this._updatePlayUI();
				this._video.addEventListener("pause", this._onVideoPauseEv);
			}
			if (!this._onVolumeChangeEv) {
				this._onVolumeChangeEv = () => this._updateVolumeUI();
				this._video.addEventListener("volumechange", this._onVolumeChangeEv);
			}
		}
	}

	_unbindMeta() {
		if (this._video && this._onTimeUpdateEv) {
			this._video.removeEventListener("timeupdate", this._onTimeUpdateEv);
			this._onTimeUpdateEv = null;
		}
		if (this._video && this._onLoadedMetadataEv) {
			this._video.removeEventListener(
				"loadedmetadata",
				this._onLoadedMetadataEv,
			);
			this._onLoadedMetadataEv = null;
		}
		if (this._video && this._onDurationChangeEv) {
			this._video.removeEventListener(
				"durationchange",
				this._onDurationChangeEv,
			);
			this._onDurationChangeEv = null;
		}
		if (this._video && this._onEmptiedEv) {
			this._video.removeEventListener("emptied", this._onEmptiedEv);
			this._onEmptiedEv = null;
		}
	}

	_unbindControls() {
		if (this._playBtn && this._onPlayClick) {
			this._playBtn.removeEventListener("click", this._onPlayClick);
			this._onPlayClick = null;
		}
		if (this._volumeBtn && this._onVolumeClick) {
			this._volumeBtn.removeEventListener("click", this._onVolumeClick);
			this._onVolumeClick = null;
		}

		if (this._video && this._onVideoPlayEv) {
			this._video.removeEventListener("play", this._onVideoPlayEv);
			this._onVideoPlayEv = null;
		}
		if (this._video && this._onVideoPauseEv) {
			this._video.removeEventListener("pause", this._onVideoPauseEv);
			this._onVideoPauseEv = null;
		}
		if (this._video && this._onVolumeChangeEv) {
			this._video.removeEventListener("volumechange", this._onVolumeChangeEv);
			this._onVolumeChangeEv = null;
		}
	}

	_updatePlayUI() {
		const isPlaying =
			!!this._video && !this._video.paused && !this._video.ended;

		this.setAttribute("data-state", isPlaying ? "playing" : "paused");

		if (this._playIcon) this._playIcon.toggleAttribute("hidden", isPlaying);
		if (this._pauseIcon) this._pauseIcon.toggleAttribute("hidden", !isPlaying);
		if (this._playBtn) {
			this._playBtn.setAttribute(
				"aria-label",
				isPlaying ? "Pause video" : "Play video",
			);
		}
	}

	_updateVolumeUI() {
		const muted = this._video ? this._video.muted : this.hasAttribute("muted");
		if (this._volOnIcon) this._volOnIcon.toggleAttribute("hidden", muted);
		if (this._volOffIcon) this._volOffIcon.toggleAttribute("hidden", !muted);
		if (this._volumeBtn) {
			this._volumeBtn.setAttribute(
				"aria-label",
				muted ? "Unmute video" : "Mute video",
			);
		}
	}

	static get observedAttributes() {
		return ["muted", "loop", "autoplay"];
	}

	attributeChangedCallback(name, _oldValue, _newValue) {
		if (!this._video) return;
		if (name === "muted") this._video.muted = this.hasAttribute("muted");
		if (name === "loop") this._video.loop = this.hasAttribute("loop");
		if (name === "autoplay" && this.hasAttribute("autoplay")) this.play();
	}

	_readPresetDuration() {
		let value =
			this.getAttribute("data-duration") ?? this.dataset.duration ?? null;
		if (!value) {
			const dataEl = this.querySelector(
				'script.video-player__data[type="application/json"]',
			);
			if (dataEl) {
				try {
					const data = JSON.parse(dataEl.textContent);
					if (data && Number.isFinite(data.duration)) value = data.duration;
				} catch (error) {
					console.error("Error parsing video player data", error);
				}
			}
		}
		const n = Number(value) / 1000; // Convert milliseconds to seconds
		return Number.isFinite(n) ? n : null;
	}

	_initMeta() {
		this._meta = this.querySelector(".video-meta");
		if (!this._meta) return;

		if (!this._meta.hasAttribute("aria-live")) {
			this._meta.setAttribute("aria-live", "polite");
			this._meta.setAttribute("aria-atomic", "true");
		}

		if (!this._timeLeftSpan) {
			const existing = this._meta.querySelector(".time-left");
			this._timeLeftSpan = existing || document.createElement("span");
			this._timeLeftSpan.classList.add("time-left");
			if (!this._timeLeftSpan.isConnected)
				this._meta.appendChild(this._timeLeftSpan);
		}
		const preset = this._readPresetDuration();
		this._timeLeftSpan.textContent =
			preset != null ? this._formatTime(preset) : "--:--";
		this._bindMeta();
	}

	_formatTime(seconds) {
		if (!Number.isFinite(seconds) || seconds < 0) return "--:--";
		const s = Math.floor(seconds % 60);
		const m = Math.floor((seconds % 3600) / 60);
		const h = Math.floor(seconds / 3600);
		const pad = (value) => value.toString().padStart(2, "0");
		return h > 0 ? `${h}:${pad(m)}:${pad(s)}s` : `${pad(m)}:${pad(s)}s`;
	}

	_updateTimeLeft() {
		if (!this._timeLeftSpan) return;
		if (!this._video) {
			const preset = this._readPresetDuration();
			this._timeLeftSpan.textContent =
				preset != null ? this._formatTime(preset) : "--:--";
			return;
		}
		const dur = this._video.duration;
		const cur = this._video.currentTime;
		const left =
			Number.isFinite(dur) && Number.isFinite(cur)
				? Math.max(0, dur - cur)
				: NaN;
		this._timeLeftSpan.textContent = Number.isFinite(left)
			? this._formatTime(left)
			: "--:--";
	}

	connectedCallback() {
		this._deferred = this.querySelector("[data-vp]") || this;
		this._posterBtn = this._deferred.querySelector(".video-player__poster");
		this._template = this._deferred.querySelector("template");

		if (!this._template) {
			console.warn("No <template> found for video player");
			return;
		}
		if (this._posterBtn) {
			this._bindPosterButton();
		} else {
			this.load();
			if (this.hasAttribute("autoplay")) this.play();
		}

		this._initControls();
		this._initMeta();

		if (!this._onTapToToggle) {
			this._onTapToToggle = (ev) => {
				if (ev.defaultPrevented) return;
				const target = ev.target;
				if (target.closest?.(".video-controls, .video-player__poster")) return;
				this.toggle();
			};
			this._deferred.addEventListener("click", this._onTapToToggle);
		}
	}

	disconnectedCallback() {
		this._unbindPosterButton();
		this._unbindControls();
		this._unbindMeta();
		if (this._onTapToToggle) {
			this._deferred.removeEventListener("click", this._onTapToToggle);
			this._onTapToToggle = null;
		}
		this._video?.pause?.();
	}

	get video() {
		return this._video;
	}

	load() {
		if (this._loaded || !this._template) return false;

		this._unbindPosterButton();
		this._posterBtn?.setAttribute("hidden", "");

		const frag = this._template.content.cloneNode(true);
		this._deferred.appendChild(frag);

		this._video = this._deferred.querySelector("video");
		if (!this._video) {
			console.warn("No <video> found for video player");
			return false;
		}

		this._video.controls = false;
		this._video.setAttribute("playsinline", "");
		this._video.setAttribute("webkit-playsinline", "");

		this._video.muted = this.hasAttribute("muted");
		this._video.loop = this.hasAttribute("loop");

		this._bindControls();
		this._updatePlayUI();
		this._updateVolumeUI();
		this._initMeta();
		this._updateTimeLeft();

		this._loaded = true;
		this.dispatchEvent(
			new CustomEvent("video:loaded", {
				detail: { video: this._video },
				bubbles: true,
				composed: true,
			}),
		);
		return true;
	}

	loadAndPlay() {
		if (!this._loaded) this.load();
		this.play();
	}

	play() {
		if (!this._loaded) this.load();
		if (!this._video) return;
		if (!this._video.paused && !this._video.ended) return;

		try {
			const p = this._video?.play?.();
			if (p && typeof p.then === "function") {
				p.then(() => {
					this.dispatchEvent(
						new CustomEvent("video:play", { bubbles: true, composed: true }),
					);
				}).catch((error) => {
					this.dispatchEvent(
						new CustomEvent("video:playerror", {
							bubbles: true,
							composed: true,
							detail: { error },
						}),
					);
				});
			} else {
				this.dispatchEvent(
					new CustomEvent("video:play", { bubbles: true, composed: true }),
				);
			}
		} catch (error) {
			this.dispatchEvent(
				new CustomEvent("video:playerror", {
					bubbles: true,
					composed: true,
					detail: { error },
				}),
			);
		}
	}

	pause() {
		this._video?.pause?.();
		this.dispatchEvent(
			new CustomEvent("video:pause", { bubbles: true, composed: true }),
		);
	}

	toggle() {
		if (!this._loaded || !this._video) {
			this.play();
			return;
		}
		this._video.paused ? this.play() : this.pause();
	}

	unload() {
		if (!this._loaded) return;
		this._video?.pause?.();
		this._video?.remove?.();
		this._unbindMeta();
		this._unbindControls();
		this._video = null;
		this._loaded = false;
		if (this._posterBtn) {
			this._posterBtn.removeAttribute("hidden");
			this._bindPosterButton();
		}
		if (this._timeLeftSpan) {
			const preset = this._readPresetDuration();
			this._timeLeftSpan.textContent =
				preset != null ? this._formatTime(preset) : "--:--";
		}
		this.dispatchEvent(
			new CustomEvent("video:unload", { bubbles: true, composed: true }),
		);
	}
}

customElements.define("video-player", VideoPlayer);

/*
HTML STRUCTURE
<video-player muted loop>
  <!-- Meta/time-left container (optional). The class will create a <span.time-left> inside. -->
  <div class="video-meta"></div>

  <!-- Controls (optional but supported by your class) -->
  <div class="video-controls">
    <button class="play-button" type="button" aria-label="Play video">
      <svg class="play-icon" aria-hidden="true"></svg>
      <svg class="pause-icon" aria-hidden="true" hidden></svg>
    </button>

    <button class="volume-button" type="button" aria-label="Mute video">
      <svg class="volume-on" aria-hidden="true"></svg>
      <svg class="volume-off" aria-hidden="true" hidden></svg>
    </button>
  </div>

  <!-- Deferred container: the class looks for [data-vp] first -->
  <div data-vp>
    <!-- Poster button: clicking this loads the <template> content and starts playback -->
    <button class="video-player__poster" type="button" aria-label="Play video">
      <!-- Your thumbnail/preview image (higher quality via srcset/sizes if you want) -->
      <img src="/path/to/poster-640.jpg" alt="Preview frame of the video">
    </button>

    <!-- Template that contains the actual <video> node (cloned on demand) -->
    <template>
      <video preload="metadata">
        <!-- choose the sources you need -->
        <source src="/path/to/video-1080.mp4" type="video/mp4">
        <!-- optional webm/ogg sources -->
      </video>
    </template>
  </div>
</video-player>
*/
